// ─── ClassMappingModal.tsx ───────────────────────────────────────────────────

import React from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { ClassData, StudentData, UDISERow, sel } from './types';

interface ClassMappingModalProps {
  visible: boolean;
  onHide: () => void;
  udiseRows: UDISERow[];
  udiseHeaders: string[];
  classes: ClassData[];
  students: StudentData[];
  udiseClassField: string;
  onUdiseClassFieldChange: (f: string) => void;
  udiseSectionField: string;
  onUdiseSectionFieldChange: (f: string) => void;
  classMap: Record<string, string>;
  onClassMapChange: (m: Record<string, string>) => void;
  onNext: () => void;
}

const ClassMappingModal: React.FC<ClassMappingModalProps> = ({
  visible, onHide,
  udiseRows, udiseHeaders, classes, students,
  udiseClassField, onUdiseClassFieldChange,
  udiseSectionField, onUdiseSectionFieldChange,
  classMap, onClassMapChange,
  onNext,
}) => {
  const udiseClasses = Array.from(new Set(
    udiseRows.map(r => r[udiseClassField] ?? '').filter(Boolean)
  ));

  const update = (udiseCls: string, dbClassId: string) =>
    onClassMapChange({ ...classMap, [udiseCls]: dbClassId });

  const mappedCount = udiseClasses.filter(uc => classMap[uc]).length;

  const header = (
    <div style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '4px 0' }}>
      <div style={{
        width: 48, height: 48, borderRadius: 13,
        background: 'linear-gradient(135deg,#1e3a8a,#3b82f6)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        flexShrink: 0, boxShadow: '0 6px 18px rgba(59,130,246,.25)',
        border: '2.5px solid white',
      }}>
        <i className="pi pi-sitemap" style={{ fontSize: 20, color: 'white' }} />
      </div>
      <div>
        <div style={{ fontSize: 18, fontWeight: 700, color: '#1e40af' }}>Map Classes</div>
        <div style={{ fontSize: 13, color: '#64748b', marginTop: 2 }}>
          Step 1 of 2 &nbsp;·&nbsp; {udiseRows.length} UDISE rows &nbsp;·&nbsp; {students.length} DB students
        </div>
      </div>
      <div style={{ marginLeft: 'auto', display: 'flex', gap: 6, alignItems: 'center' }}>
        <div style={{ width: 28, height: 6, borderRadius: 3, background: '#3b82f6' }} />
        <div style={{ width: 28, height: 6, borderRadius: 3, background: '#dbeafe' }} />
      </div>
    </div>
  );

  return (
    <Dialog
      visible={visible} onHide={onHide} header={header}
      style={{ width: '94vw', maxWidth: 900, borderRadius: 22, overflow: 'hidden' }}
      contentStyle={{ padding: '18px 24px 8px', background: 'linear-gradient(180deg,#f8fafc,#f1f5f9)' }}
      headerStyle={{ background: 'white', borderBottom: '1px solid #e5e7eb', padding: '18px 24px', borderRadius: '22px 22px 0 0' }}
      modal draggable={false} resizable={false} blockScroll dismissableMask
    >
      <style>{`
        .cm-select {
          width: 100%; padding: 8px 10px; border-radius: 10px;
          border: 1.5px solid #dbeafe; background: white;
          font-size: 13px; color: #1e3a8a; outline: none; cursor: pointer;
          transition: border-color .15s;
        }
        .cm-select:focus { border-color: #3b82f6; }
        .cm-field-label {
          font-size: 11px; font-weight: 600; color: #64748b;
          display: block; margin-bottom: 5px;
          text-transform: uppercase; letter-spacing: .4px;
        }
        .cm-map-row {
          display: grid; grid-template-columns: 1fr 28px 1fr;
          gap: 10px; align-items: center;
          padding: 10px 13px; border-radius: 12px;
          border: 1.5px solid #e5e7eb; background: white;
          transition: all .15s;
        }
        .cm-map-row.mapped   { border-color: #bfdbfe; background: #f0f9ff; }
        .cm-map-row.unmapped { border-color: #fde68a; background: #fffbeb; }
      `}</style>

      {/* Column selectors card */}
      <div style={{
        padding: '14px 16px', borderRadius: 14,
        background: 'white', border: '1.5px solid #e5e7eb',
        boxShadow: '0 1px 4px rgba(0,0,0,.04)', marginBottom: 16,
      }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: '#1e40af', marginBottom: 12, display: 'flex', alignItems: 'center', gap: 7 }}>
          <i className="pi pi-table" style={{ fontSize: 13, color: '#3b82f6' }} />
          Which columns represent Class &amp; Section?
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <div>
            <label className="cm-field-label">CSV Class Column</label>
            <select className="cm-select" value={udiseClassField} onChange={e => onUdiseClassFieldChange(e.target.value)}>
              {udiseHeaders.map(h => <option key={h} value={h}>{h}</option>)}
            </select>
          </div>
          <div>
            <label className="cm-field-label">
              CSV Section &nbsp;<span style={{ color: '#94a3b8', fontWeight: 400, textTransform: 'none' }}>(optional)</span>
            </label>
            <select className="cm-select" value={udiseSectionField} onChange={e => onUdiseSectionFieldChange(e.target.value)}>
              <option value="">-- No Section --</option>
              {udiseHeaders.map(h => <option key={h} value={h}>{h}</option>)}
            </select>
          </div>
        </div>
      </div>

      {/* Section heading + count badge */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: '#1e40af', display: 'flex', alignItems: 'center', gap: 7 }}>
          <i className="pi pi-arrows-h" style={{ fontSize: 13, color: '#3b82f6' }} />
          Map CSV Classes → School DB
        </div>
        <span style={{
          fontSize: 12, fontWeight: 600, padding: '3px 10px', borderRadius: 20,
          background: mappedCount > 0 ? 'rgba(59,130,246,.12)' : '#f3f4f6',
          color: mappedCount > 0 ? '#1e40af' : '#9ca3af',
        }}>
          {mappedCount} / {udiseClasses.length} mapped
        </span>
      </div>

      {udiseClasses.length === 0 ? (
        <div style={{ fontSize: 13, color: '#94a3b8', textAlign: 'center', padding: '20px 0' }}>
          No class values found with the selected column
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxHeight: 240, overflowY: 'auto', paddingRight: 2, marginBottom: 14 }}>
          {udiseClasses.map(uc => {
            const currentVal = classMap[uc] ?? '';
            return (
              <div key={uc} className={`cm-map-row ${currentVal ? 'mapped' : 'unmapped'}`}>
                <div style={{ fontSize: 13, fontWeight: 700, color: '#1e40af', fontFamily: 'monospace', display: 'flex', alignItems: 'center', gap: 6 }}>
                  <i className="pi pi-file" style={{ fontSize: 11, color: '#64748b' }} />
                  {uc}
                </div>
                <div style={{ display: 'flex', justifyContent: 'center' }}>
                  <i className="pi pi-arrow-right" style={{ fontSize: 11, color: currentVal ? '#3b82f6' : '#d1d5db' }} />
                </div>
                <select
                  className="cm-select"
                  value={currentVal}
                  onChange={e => update(uc, e.target.value)}
                  style={{ borderColor: currentVal ? '#bfdbfe' : '#fde68a' }}
                >
                  <option value="">-- Select DB Class --</option>
                  {classes.map(c => <option key={c.id} value={c.id}>{c.class_name}</option>)}
                </select>
              </div>
            );
          })}
        </div>
      )}

      <div style={{ fontSize: 11, color: '#94a3b8', marginBottom: 18 }}>
        <i className="pi pi-info-circle" style={{ marginRight: 4, color: '#3b82f6' }} />
        Roman numerals (IX, X etc.) are auto-matched where possible.
      </div>

      {/* Footer */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 0 6px', borderTop: '1px solid #f0f0f0' }}>
        <div style={{ fontSize: 13, color: '#64748b' }}>
          {mappedCount === 0
            ? 'No classes mapped yet'
            : <><span style={{ fontWeight: 700, color: '#3b82f6' }}>{mappedCount}</span> class{mappedCount !== 1 ? 'es' : ''} mapped</>
          }
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <Button label="Cancel" icon="pi pi-times" className="p-button-outlined"
            style={{ borderRadius: 10, fontSize: 13 }} onClick={onHide} />
          <Button
            label="Next: Field Mapping →" icon="pi pi-arrow-right" iconPos="right"
            style={{ borderRadius: 10, fontSize: 13, background: 'linear-gradient(135deg,#1e40af,#3b82f6)', border: 'none' }}
            onClick={onNext}
          />
        </div>
      </div>
    </Dialog>
  );
};

export default ClassMappingModal;